import {
  useCallback,
  useState,
  useEffect,
  ChangeEvent,
  FC,
  useRef
} from 'react'
import MikeSnackbar from '@mike/mike-shared-frontend/mike-snackbar'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { useOidc, useOidcIdToken, useOidcAccessToken } from '@axa-fr/react-oidc'
import { getNotAuthErrors } from '../../helpers/errors'
import AppLayout from '../AppLayout'
import PageNotFound from '../PageNotFound'
import APIAccess from '../APIAccess'
import ConnectedProjectContent from '../../containers/ConnectedProjectContent'
import ConnectedRecycleBinItemsDeletedByMe from '../../containers/ConnectedRecycleBinItemsDeletedByMe'
import Users from '../Users'
import AppNotificationSnack from '../AppNotificationSnack'
import LinkNotificationSnack from '../LinkNotificationSnack'
import MemberReportReadySnack from '../MemberReportDialog/MemberReportReadySnack'
import { CATEGORIES } from '../../helpers/project'
import { Typography } from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { setUser, setIsAuthenticated } from '../../actions/auth'
import { showNotification } from '../../actions/ui'
import { getUserProfile } from '../../apis'
import { useDispatch } from 'react-redux'
import LandingPage from '../LandingPage'
import PrivateRoute from '../PrivateRoute'
import ProjectAccessUsers from '../ProjectAccessUsers'
import ProjectAccessAPI from '../ProjectAccessAPI'
import SiteDetails from '../SiteDetails'
import SiteCpuUsage from '../SiteCpuUsage'
import SiteStorageUsage from '../SiteStorageUsage'
import Applications from '../Applications'
import SystemStatusMessages from '../SystemStatusMessages'
import { canManageSystemStatus } from '../../utils/systemStatusAccess'
import RootProject from '../RootProject'
import qs from 'qs'
import { useTypedSelector } from '../../reducers'
import {
  setAccessTokenGetter,
  cacheTenantAccessToken
} from '../../actions/tokenBridge'
import { setAuthenticatedUser } from '../../apis/appInsights'

export const DEFAULT_PROJECTS_VIEW = CATEGORIES.MY

type ErrorType = { id: string; message: string }
type ProjectType = { id: string; name: string; [key: string]: any }
type SupportType = { [key: string]: any }
type TutorialType = { [key: string]: any }

type AppProps = {
  errors: ErrorType[]
  linkNotification?: string
  notification?: string
  linkToProject?: ProjectType
  support: SupportType
  tutorial: TutorialType
  onCloseError: (id: string) => void
  onCloseLinkNotification: () => void
  onClosePopupReminder: () => void
  onSurpressPopupReminder: (surpress: boolean) => void
  onDismissProgressListItem: (item: { id: string }) => void
  appStatus?: Record<string, any>
  popupReminderIsOpen: boolean
  surpressPopupReminder: boolean
  progressItems?: any[]
  loadingApps?: boolean
  tenants?: any[]
  openCreateTenant?: boolean
  onCreateTenantCloseClick: () => void
  onCreateTenant: (data: any) => void
  onOpenCreateTenant: () => void
  isAdmin?: boolean
  isBackOffice?: boolean
  features?: any[]
  currentProject?: Record<string, any>
}

const App: FC<AppProps> = ({
  errors,
  notification,
  linkNotification,
  linkToProject,
  support,
  tutorial,
  onCloseError,
  onCloseLinkNotification,
  onClosePopupReminder,
  onSurpressPopupReminder,
  popupReminderIsOpen,
  surpressPopupReminder,
  progressItems,
  loadingApps,
  tenants,
  openCreateTenant,
  onCreateTenantCloseClick,
  onCreateTenant,
  onOpenCreateTenant,
  isAdmin,
  isBackOffice,
  features,
  currentProject
}) => {
  const dispatch = useDispatch()
  const otherErrors = getNotAuthErrors(errors)
  const changeSiteKickoff = useRef(false)

  const [lastOtherError, setLastOtherError] = useState<ErrorType | null>(null)
  const { isAuthenticated, login } = useOidc()
  const { idToken, idTokenPayload } = useOidcIdToken()
  const { accessToken } = useOidcAccessToken()

  const location = useLocation()
  const { search, pathname } = location

  const user = useTypedSelector(state => state.auth.user)
  const [currentUser, setCurrentUser] = useState<any>(null)

  const accessTokenRef = useRef<string | null>(null)
  accessTokenRef.current = accessToken ?? null
  const getterInstalled = useRef(false)
  if (!getterInstalled.current) {
    getterInstalled.current = true
    setAccessTokenGetter(() => accessTokenRef.current)
  }

  useEffect(() => {
    const tid =
      (idTokenPayload as any)?.TenantId || (idTokenPayload as any)?.tenantId
    if (accessToken && tid) {
      cacheTenantAccessToken(String(tid), accessToken)
    }
  }, [accessToken, idTokenPayload])

  useEffect(() => {
    if (idToken && idTokenPayload) {
      const u = getUserProfile(idTokenPayload)
      dispatch(setUser(u))
      setCurrentUser(u)
    }
  }, [dispatch, idToken, idTokenPayload])

  useEffect(() => {
    const params = new URLSearchParams(search)
    const wantsChangeSite = params.get('changeSite') === '1'

    if (wantsChangeSite && !changeSiteKickoff.current) {
      changeSiteKickoff.current = true
      params.delete('changeSite')
      const newSearch = params.toString()
      const newUrl =
        pathname +
        (newSearch ? `?${newSearch}` : '') +
        (window.location.hash || '')
      window.history.replaceState({}, document.title, newUrl)
    }

    if (user) {
      if (search) {
        const pathAndSearch = search.length > 1 ? pathname + search : pathname
        const parameters = qs.parse(search.replace('?', '')) as any
        const postBack = parameters?.postBack === '1'

        if (
          postBack &&
          parameters?.tenantId &&
          parameters.tenantId !== user.tenantId
        ) {
          const extras = {
            prompt: 'none',
            tenantId: parameters.tenantId.toString()
          }
          login(pathAndSearch, extras)
          return
        }
      }
    }
  }, [user, login, search, pathname])

  useEffect(() => {
    const params = new URLSearchParams(search)
    const postBack = params.get('postBack') === '1'
    const requestedTenantId = params.get('tenantId')

    if (!postBack || !requestedTenantId) return

    const tokenTenantId =
      (idTokenPayload as any)?.TenantId || (idTokenPayload as any)?.tenantId

    if (accessToken && tokenTenantId === requestedTenantId) {
      cacheTenantAccessToken(String(requestedTenantId), accessToken)
      try {
        window.opener?.postMessage(
          {
            type: 'tenant-auth-ok',
            tenantId: requestedTenantId,
            token: accessToken
          },
          window.location.origin
        )
      } finally {
        setTimeout(() => window.close(), 150)
      }
      return
    }

    if (requestedTenantId && tokenTenantId !== requestedTenantId) {
      const pathAndSearch = search.length > 1 ? pathname + search : pathname
      login(pathAndSearch, { tenantId: String(requestedTenantId) })
    }
  }, [search, pathname, accessToken, idTokenPayload, login])

  useEffect(() => {
    if (user) {
      const _paq = (window._paq = window._paq || [])
      _paq.push(['setUserId', user.email])
      _paq.push(['trackPageView'])
      _paq.push(['enableHeartBeatTimer'])

      setAuthenticatedUser(user.id, user.tenantId)
    }
  }, [user])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setIsAuthenticated(isAuthenticated))
    }
  }, [dispatch, isAuthenticated, isBackOffice])

  useEffect(() => {
    const count = otherErrors ? otherErrors.length : 0
    setLastOtherError(count > 0 ? otherErrors[count - 1] : null)
  }, [otherErrors])

  const onRemoveError = useCallback(() => {
    lastOtherError && onCloseError(lastOtherError?.id)
  }, [lastOtherError, onCloseError])

  const handleSurpressReminder = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    onSurpressPopupReminder(checked)
  }

  if (!isAuthenticated) {
    return <LandingPage />
  }

  return (
    <>
      <AppNotificationSnack
        open={!!notification}
        message={notification}
        onClose={() => dispatch(showNotification(null))}
      />

      <MemberReportReadySnack />

      <MikeSnackbar
        open={!!lastOtherError}
        mikeSnackbarType={'error'}
        message={lastOtherError && lastOtherError.message}
        action={'OK'}
        mikeOnAction={onRemoveError}
        onClose={onRemoveError}
        autoHideDuration={null}
      />

      <MikeSnackbar
        open={popupReminderIsOpen}
        mikeSnackbarType={'regular'}
        action={'OK'}
        mikeOnAction={onClosePopupReminder}
        onClose={onClosePopupReminder}
        autoHideDuration={null}
      >
        <>
          <Typography variant="body1">
            {
              'Turn off the pop-up blocker in your browser to allow download of files'
            }
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={surpressPopupReminder}
                onChange={handleSurpressReminder}
                name={'surpressReminder'}
              />
            }
            label={'Do not show this again'}
          />
        </>
      </MikeSnackbar>

      <>
        <LinkNotificationSnack
          message={linkNotification}
          onCloseCallBack={onCloseLinkNotification}
          open={!!linkToProject}
          project={linkToProject}
        />

        <AppLayout
          user={currentUser}
          support={support as any}
          tutorial={tutorial as any}
          progressItems={progressItems}
          tenants={tenants}
          openCreateTenant={openCreateTenant}
          onCreateTenantCloseClick={onCreateTenantCloseClick}
          onCreateTenant={onCreateTenant}
          onOpenCreateTenant={onOpenCreateTenant}
          isAdmin={isAdmin}
          loadingApps={loadingApps}
          features={features}
          currentProject={currentProject}
        >
          <Routes>
            <Route
              path="/project/:id/access/api"
              element={<ProjectAccessAPI />}
            />
            <Route
              path="/site/:id/access/users"
              element={<ProjectAccessUsers />}
            />
            <Route path="/site/:id/access/api" element={<ProjectAccessAPI />} />
            <Route path="/site/:id" element={<ConnectedProjectContent />} />
            <Route path="/project/:id" element={<ConnectedProjectContent />} />
            <Route path="/site" element={<RootProject />} />
            <Route path="/project" element={<RootProject />} />
            <Route
              path="/recycle-bin/:id"
              element={<ConnectedRecycleBinItemsDeletedByMe />}
            />
            <Route
              path="/site-access/users"
              element={
                <PrivateRoute isSignedIn={!!isAdmin} element={<Users />} />
              }
            />
            <Route
              path="/site-access/api"
              element={
                <PrivateRoute isSignedIn={!!isAdmin} element={<APIAccess />} />
              }
            />
            <Route
              path="/site-access/applications"
              element={
                <PrivateRoute
                  isSignedIn={!!isAdmin}
                  element={<Applications />}
                />
              }
            />
            <Route
              path="/site-access"
              element={<Navigate to="/site-access/users" replace />}
            />
            <Route
              path="/backoffice/system-status"
              element={
                <PrivateRoute
                  isSignedIn={canManageSystemStatus(!!isBackOffice)}
                  element={<SystemStatusMessages />}
                />
              }
            />

            <Route
              path="/site-info/details"
              element={
                <PrivateRoute
                  isSignedIn={!!isAdmin}
                  element={<SiteDetails />}
                />
              }
            />
            <Route
              path="/site-info/cpu"
              element={
                <PrivateRoute
                  isSignedIn={!!isAdmin}
                  element={<SiteCpuUsage />}
                />
              }
            />
            <Route
              path="/site-info/storage"
              element={
                <PrivateRoute
                  isSignedIn={!!isAdmin}
                  element={<SiteStorageUsage />}
                />
              }
            />
            <Route
              path="/site-info"
              element={<Navigate to="/site-info/details" replace />}
            />

            <Route path="/" element={<Navigate to="/site" replace />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </AppLayout>
      </>
    </>
  )
}

export default App
